import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/')({ component: AdminHome });

type Stats = {
  therapistsCount: number;
  clientsCount: number;
  sessionsCount: number;
  pendingNFCount: number;
  openCyclesCount: number;
  awaitingPayoutCount: number;
  publishedReviewsCount: number;
  leadsCount: number;
  pendingTherapistVerification: number;
  pendingRefundsCount: number;
};

function AdminHome() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.get('admin/stats').json<Stats>(),
  });

  if (isLoading || !data) {
    return (
      <>
        <AdminPageHeader title="Visão geral" subtitle="Resumo da plataforma" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-xl h-24 animate-pulse" />
          ))}
        </div>
      </>
    );
  }

  const cards: { label: string; value: number; href: string; color: string; alert?: boolean }[] = [
    { label: 'Profissionais',       value: data.therapistsCount,        href: '/admin/terapeutas',    color: 'teal' },
    { label: 'Clientes',            value: data.clientsCount,           href: '/admin/clientes',      color: 'indigo' },
    { label: 'Sessões',             value: data.sessionsCount,          href: '/admin/sessoes',       color: 'slate' },
    { label: 'NFs em análise',      value: data.pendingNFCount,         href: '/admin/notas-fiscais', color: 'amber',
      alert: data.pendingNFCount > 0 },
    { label: 'Aguardando repasse',  value: data.awaitingPayoutCount,    href: '/admin/repasse',       color: 'amber',
      alert: data.awaitingPayoutCount > 0 },
    { label: 'Reembolsos pendentes', value: data.pendingRefundsCount,   href: '/admin/cancelamentos', color: 'coral',
      alert: data.pendingRefundsCount > 0 },
    { label: 'Ciclos abertos',      value: data.openCyclesCount,        href: '/admin/financeiro',    color: 'slate' },
    { label: 'Avaliações públicas', value: data.publishedReviewsCount,  href: '/admin/avaliacoes',    color: 'coral' },
    { label: 'Verificações pendentes de CRP', value: data.pendingTherapistVerification, href: '/admin/terapeutas', color: 'amber',
      alert: data.pendingTherapistVerification > 0 },
    { label: 'Leads (banner Beta)', value: data.leadsCount,             href: '/admin/leads',         color: 'slate' },
  ];

  const colorClass: Record<string, string> = {
    teal:   'border-teal-200 bg-teal-50/50 hover:border-teal-400',
    indigo: 'border-indigo-200 bg-indigo-50/50 hover:border-indigo-400',
    slate:  'border-slate-200 bg-slate-50/50 hover:border-slate-400',
    amber:  'border-amber-200 bg-amber-50/50 hover:border-amber-400',
    coral:  'border-coral-200 bg-coral-50/50 hover:border-coral-400',
  };

  return (
    <>
      <AdminPageHeader title="Visão geral" subtitle="Resumo operacional da plataforma" />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.href as any}>
            <Card className={cn('border-2 transition cursor-pointer', colorClass[c.color])}>
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-3xl font-extrabold text-slate-900 tabular-nums">{c.value}</div>
                  {c.alert && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 font-semibold uppercase tracking-wide">
                      Ação
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mt-1 font-semibold">{c.label}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </>
  );
}
